"use client";

import * as React from "react";
import { ApiProvider, AuthToken, useApiReady } from "./api/api-provider.js";
import { useAuthenticationInformation } from "./api/endpoint.js";
import {
  UserSecurityError,
  UserSecurityLoading,
  UserSecurity as UserSecurityPresentational,
} from "./lib/user-security.js";
import type {
  UserSecurityErrorProps,
  UserSecurityLoadingProps,
} from "./lib/user-security.js";
import { useWorkOsApiUrl } from "./lib/widgets-context.js";
import { ErrorBoundary } from "./lib/error-boundary.js";
import type { WidgetRootDomProps } from "./lib/utils.js";

interface UserSecurityProps extends WidgetRootDomProps {
  authToken: AuthToken;
}

const UserSecurity: React.FC<UserSecurityProps> = ({
  authToken,
  ...domProps
}) => {
  const baseUrl = useWorkOsApiUrl();

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <UserSecurityError error={error} {...domProps} />
      )}
    >
      <ApiProvider
        widgetType="user-security"
        authToken={authToken}
        baseUrl={baseUrl}
      >
        <UserSecurityImpl {...domProps} />
      </ApiProvider>
    </ErrorBoundary>
  );
};

const UserSecurityImpl = (props: WidgetRootDomProps) => {
  const isApiReady = useApiReady();
  const {
    data: settings,
    isLoading,
    isError,
    isSuccess,
    error,
  } = useAuthenticationInformation({
    query: { enabled: isApiReady },
  });

  if (!isApiReady || isLoading) {
    return <UserSecurityLoading {...props} />;
  }

  if (isError || !isSuccess) {
    return <UserSecurityError error={error} {...props} />;
  }

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <UserSecurityError error={error} {...props} />
      )}
    >
      <UserSecurityPresentational settings={settings.data} {...props} />
    </ErrorBoundary>
  );
};

export type {
  UserSecurityProps,
  UserSecurityLoadingProps,
  UserSecurityErrorProps,
};
export { UserSecurity, UserSecurityLoading, UserSecurityError };
